import React, { useState, useEffect, useMemo } from "react";
import MetaTags from "react-meta-tags";
import { useSelector } from "react-redux";
import "../featureDesign.scss";
import FeatureKpiHeader from "../FeatureKpiHeader";
import { useRiskTrendHistory, severityCounters } from "../riskTrend";
import FeatureTableCard from "../FeatureTableCard";
import ScanProfileTabPane from "../ScanProfileTabPane";

const DNSLookup = () => {
  const [productData, setProductData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [featureNameID, setfeatureNameID] = useState("");
  const [ScanProfileId, setScanProfileId] = useState("");
  const [Checks, setChecks] = useState([]);
  const [activeTab, setActiveTab] = useState("data");
  const [recordTypeFilter, setRecordTypeFilter] = useState("ALL");

  const [criticalCardPrice, setCriticalCardPrice] = useState(0);
  const [highCardPrice, setHighCardPrice] = useState(0);
  const [mediumCardPrice, setMediumCardPrice] = useState(0);
  const [lowCardPrice, setLowCardPrice] = useState(0);
  const [informationalCardPrice, setInformationalCardPrice] = useState(0);

  const userID = localStorage.getItem("userID");
  const orgID = localStorage.getItem("orgID");
  const token = localStorage.getItem("token");
  const siteDomain = localStorage.getItem("domain") || "scanme.nmap.org";

  const { layoutMode } = useSelector((state) => ({
    layoutMode: state.Layout.layoutMode,
  }));
  const isDark =
    layoutMode === "dark" ||
    (typeof document !== "undefined" && document.body.getAttribute("data-layout-mode") === "dark");
  const theme = isDark ? "dark" : "light";

  // Real per-day history for the Risk trend chart
  const trendHistory = useRiskTrendHistory(165112129704677, severityCounters.dns, loading);

  useEffect(() => {
    async function checkAuthAndLoad() {
      try {
        if (token) {
          const authRes = await fetch(`${process.env.REACT_APP_DEFAULTPATH}jwtauth`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          });
          const authData = await authRes.json();
          if (authData && authData.status === false) {
            window.location.href = "/login";
            return;
          }
        }
      } catch (e) {
        console.warn("Auth check error:", e);
      }
      getDNSLookupData();
      getScanProfileString();
    }
    checkAuthAndLoad();
  }, []);

  async function getDNSLookupData() {
    setLoading(true);
    const featureIDForServer = 165112129704677;
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}taskData`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userID, orgID, featureIDForServer }),
      });
      const data = await response.json();

      if (data && data.data && data.data.Data && data.data.Data.length > 0) {
        const mainData = data.data.Data[0];
        let c = 0, h = 0, m = 0, l = 0, inf = 0;
        const combined = [];

        const recordTypes = ["A", "AAAA", "CNAME", "SOA", "MX", "NS", "TXT", "CAA"];

        recordTypes.forEach((typeKey) => {
          const list = mainData[typeKey];
          if (Array.isArray(list)) {
            list.forEach((rec, idx) => {
              const rawRisk = rec.Risk || "Low";
              const rLower = String(rawRisk).toLowerCase();
              if (rLower.includes("crit")) c++;
              else if (rLower.includes("high")) h++;
              else if (rLower.includes("med")) m++;
              else if (rLower.includes("info")) inf++;
              else l++;

              let valueDisplay = "-";
              if (rec.IPv4) valueDisplay = rec.IPv4;
              else if (rec.IPv6) valueDisplay = rec.IPv6;
              else if (rec.Target) valueDisplay = rec.Target;
              else if (rec.MName || rec.RName) valueDisplay = `${rec.MName || "-"} / ${rec.RName || "-"}`;
              else if (Array.isArray(rec.Strings)) valueDisplay = rec.Strings.join(", ");
              else if (rec.Strings) valueDisplay = String(rec.Strings);
              else if (rec.Text) valueDisplay = String(rec.Text);
              else if (rec.Value) valueDisplay = String(rec.Value);

              combined.push({
                ID: rec.ID || `${typeKey}-${idx + 1}`,
                Type: rec.Type || typeKey,
                Value: valueDisplay,
                TTL: rec.TTL != null ? String(rec.TTL) : "-",
                Risk: rawRisk,
              });
            });
          }
        });

        setProductData(combined);
        setCriticalCardPrice(c);
        setHighCardPrice(h);
        setMediumCardPrice(m);
        setLowCardPrice(l);
        setInformationalCardPrice(inf);
      } else {
        setProductData([]);
        setCriticalCardPrice(0);
        setHighCardPrice(0);
        setMediumCardPrice(0);
        setLowCardPrice(0);
        setInformationalCardPrice(0);
      }
    } catch (err) {
      console.error("Error fetching DNS Lookup data:", err);
      setProductData([]);
    } finally {
      setLoading(false);
    }
  }

  async function getScanProfileString() {
    const featureIDForServer = 165112129704677;
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}updatesubdomaintable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userID, orgID, featureIDForServer }),
      });
      const data = await response.json();
      if (data && data.data) {
        setScanProfileId(data.data.ScanProfileID || "");
        setfeatureNameID(data.data.FeatureName || "");
        setChecks(data.data.Checks || []);
      }
    } catch (err) {
      console.warn("ScanProfile fetch error:", err);
    }
  }

  async function updateProfileString(string, id, riskValue, index, stringID, taskType) {
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}updatestring`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stringID,
          string,
          id,
          riskValue,
          index,
          taskType,
        }),
      });
      const data = await response.json();
      if (data.status === "ok") {
        getScanProfileString();
      }
    } catch (e) {
      console.error("Update string error:", e);
    }
  }

  const getBadgeClass = (sev) => {
    switch (String(sev).toLowerCase()) {
      case "critical": return "feature-badge badge-critical";
      case "high": return "feature-badge badge-high";
      case "medium":
      case "med": return "feature-badge badge-medium";
      case "low": return "feature-badge badge-low";
      case "informational":
      case "info": return "feature-badge badge-info";
      default: return "feature-badge badge-info";
    }
  };

  const totalFindings =
    criticalCardPrice + highCardPrice + mediumCardPrice + lowCardPrice + informationalCardPrice;

  const { score, riskLabel } = useMemo(() => {
    if (totalFindings === 0) return { score: 100, riskLabel: "Low" };
    const deduction =
      criticalCardPrice * 12 + highCardPrice * 6 + mediumCardPrice * 2 + lowCardPrice * 1;
    const rawScore = 100 - deduction;

    let calculatedScore = 98.42;
    let label = "Informational";

    if (criticalCardPrice > 0) {
      calculatedScore = Math.max(15, Math.min(39, rawScore));
      label = "Critical";
    } else if (highCardPrice > 0) {
      calculatedScore = Math.max(40, Math.min(59, rawScore));
      label = "High";
    } else if (mediumCardPrice > 0) {
      calculatedScore = Math.max(60, Math.min(84, rawScore));
      label = "Medium";
    } else if (lowCardPrice > 0) {
      calculatedScore = 98.42;
      label = "Low";
    } else {
      calculatedScore = 98.42;
      label = "Low";
    }

    return { score: calculatedScore, riskLabel: label };
  }, [totalFindings, criticalCardPrice, highCardPrice, mediumCardPrice, lowCardPrice]);

  const filteredData =
    recordTypeFilter === "ALL"
      ? productData
      : productData.filter((item) => item.Type === recordTypeFilter);

  const columns = [
    { key: "ID", label: "ID", isId: true },
    {
      key: "Type",
      label: "Type",
      isMono: true,
      render: (val) => (
        <span
          style={{
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: "4px",
            background: "var(--slate-800)",
            color: "var(--brand-primary)",
            fontWeight: 600,
            fontSize: 12,
            fontFamily: "var(--font-mono)",
          }}
        >
          {val}
        </span>
      ),
    },
    { key: "Value", label: "Record Value / Target", isMono: true },
    { key: "TTL", label: "TTL", isMono: true },
    {
      key: "Risk",
      label: "Risk",
      render: (val) => <span className={getBadgeClass(val)}>{val || "Low"}</span>,
    },
  ];

  const recordTypesList = ["ALL", "A", "AAAA", "CNAME", "MX", "NS", "TXT", "SOA"];

  return (
    <React.Fragment>
      <MetaTags>
        <title>Exposed DNS Records | Glogix</title>
      </MetaTags>

      <div className="page-content feature-page-root" data-theme={theme}>
        <div className="feature-container">
          <FeatureKpiHeader
            title="DNS Details"
            breadcrumbItem="DNS Details"
            siteTitle={siteDomain}
            score={score}
            riskLabel={riskLabel}
            counts={{
              critical: criticalCardPrice,
              high: highCardPrice,
              medium: mediumCardPrice,
              low: lowCardPrice,
              info: informationalCardPrice,
            }}
            loading={loading}
            trendHistory={trendHistory}
          />

          <FeatureTableCard
            tabs={[
              { key: "data", label: "Exposed DNS", count: productData.length },
              { key: "profile", label: "Scan Profile" },
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            searchPlaceholder="Search"
            columns={columns}
            data={filteredData}
          >
            {activeTab === "data" && (
              <div style={{ display: "flex", gap: 6, padding: "8px 16px 0", flexWrap: "wrap" }}>
                {recordTypesList.map((t) => (
                  <button
                    key={t}
                    onClick={() => setRecordTypeFilter(t)}
                    style={{
                      border: "1px solid",
                      borderColor: recordTypeFilter === t ? "var(--brand-primary)" : "var(--border-default)",
                      background: recordTypeFilter === t ? "rgba(47, 111, 237, 0.15)" : "var(--bg-surface-muted)",
                      color: recordTypeFilter === t ? "var(--brand-primary)" : "var(--fg-secondary)",
                      padding: "4px 10px",
                      borderRadius: "var(--radius-md)",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}

            {activeTab === "profile" && (
              <ScanProfileTabPane
                featureName={featureNameID || "Exposed DNS Profile"}
                checks={Checks}
                onDelete={(id, keyword) =>
                  updateProfileString(keyword, ScanProfileId, "Low", 0, id, "delete")
                }
                onSave={(id, keyword, risk) =>
                  updateProfileString(keyword, ScanProfileId, risk, 0, id, "updateRisk")
                }
                onAdd={(keyword, risk) =>
                  updateProfileString(keyword, ScanProfileId, risk, 0, null, "add")
                }
              />
            )}
          </FeatureTableCard>
        </div>
      </div>
    </React.Fragment>
  );
};

export default DNSLookup;
